/**
 * Legacy Project Migration - Process Scribe File
 *
 * This module was created using Claude AI and Qwen 3 AI models along with manual edits.
 * The original POC can be found along with the AI Spec.md file can be found here:
 * https://github.com/Sparkol/scribe-converter/blob/main/SPEC.md
 */

import type JSZip from 'jszip';
import uuid from 'uuid/v4';

import ScribeModel from 'js/models/ScribeModel';
import { AudioClip, AudioRole, ScribeCameraElement, VSElementModel } from 'js/types';
import ScribeImageElementModel from 'js/models/ScribeImageElementModel';
import ScribeTextElementModel from 'js/models/ScribeTextElementModel';
import { toSeconds } from 'js/editor/PlayerContext/timingHelpers';
import { fontFilesManifest } from 'js/playback/helpers/fontFilesManifest.ts';

let JSZipConstructor: typeof JSZip | null = null;
async function loadJSZip() {
  if (!JSZipConstructor) {
    const module = await import('jszip');
    JSZipConstructor = module.default;
  }

  return JSZipConstructor;
}

// Convert milliseconds to seconds
function msToSec(ms: number): number {
  return toSeconds(ms);
}

function intToHexColor(intColor: number): string {
  const hex = intColor.toString(16).padStart(6, '0');
  return `#${hex}`;
}

function base64ToBytes(base64: string): Uint8Array | null {
  try {
    const normalized = base64
      .replace(/\s+/g, '')
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

function readGifDimensions(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 10) return null;
  const sig = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3], bytes[4], bytes[5]);
  if (sig !== 'GIF87a' && sig !== 'GIF89a') return null;
  const width = bytes[6] | (bytes[7] << 8);
  const height = bytes[8] | (bytes[9] << 8);
  if (width <= 0 || height <= 0) return null;
  return { width, height };
}

// Generate a UUID (simplified version)
function generateUUID(): string {
  return uuid();
}

export type BlobDict = {
  [id: string]: Blob;
};
export type AudioBlobDict = {
  [id: string]: {
    file: Blob;
    role: AudioRole;
    assetName: string;
  };
};

// Fallback natural glyph height when no vsfont is bundled or the font can't be matched.
// vsfont glyphs are stored in a padded SVG viewbox (typically 175); the *visible* glyph
// occupies roughly the vsfont's @lineHeight (~71 for Staatliches). We fall back to a
// mid-range visible height that produces reasonable text sizes across common fonts.
const VSFONT_FALLBACK_VISIBLE_HEIGHT = 90;
// TLF text uses ~1.2× font size for line box height (getLineMetrics().height).
const TLF_LINE_HEIGHT_RATIO = 1.2;

type VSFontChar = {
  code: number;
  sizX: number;
  sizY: number;
  advance: number;
  offsetX: number;
  offsetY: number;
};

type VSFontMetrics = {
  fontName: string;
  spaceWidth: number;
  lineHeight: number;
  charSpace: number;
  visibleHeight: number;
  chars: Map<number, VSFontChar>;
};

// Parse all bundled *.vsfont entries (each is a ZIP containing font.xml).
async function loadVSFonts(zip: JSZip): Promise<Map<string, VSFontMetrics>> {
  const fonts = new Map<string, VSFontMetrics>();
  for (const [path, entry] of Object.entries(zip.files)) {
    if (!path.toLowerCase().endsWith('.vsfont')) continue;
    try {
      const buf = await entry.async('uint8array');
      const JSZip = await loadJSZip();
      const inner = await new JSZip().loadAsync(buf);
      const xmlEntry = inner.file('font.xml');
      if (!xmlEntry) continue;
      const xmlText = await xmlEntry.async('string');
      const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
      const rootEl = doc.documentElement;
      const fontName = rootEl.getAttribute('fontName') || '';
      const spaceWidth = parseFloat(rootEl.getAttribute('spaceWidth') || '25');
      const lineHeight = parseFloat(rootEl.getAttribute('lineHeight') || '0');
      const charSpace = parseFloat(rootEl.getAttribute('charSpace') || '5');
      const chars = new Map<number, VSFontChar>();
      for (const ch of Array.from(rootEl.getElementsByTagName('character'))) {
        const code = parseInt(ch.getAttribute('charCode') || '0', 10);
        const sizX = parseFloat(ch.getAttribute('sizX') || '0');
        const sizY = parseFloat(ch.getAttribute('sizY') || '0');
        chars.set(code, {
          code,
          sizX,
          sizY,
          advance: parseFloat(ch.getAttribute('advance') || String(sizX)),
          offsetX: parseFloat(ch.getAttribute('offsetX') || '0'),
          offsetY: parseFloat(ch.getAttribute('offsetY') || '0')
        });
      }
      // vsfont lineHeight tracks the visible glyph height; fall back to a fraction
      // of the padded sizY (~40% empirically) when the font omits it.
      let visibleHeight = lineHeight;
      if (!visibleHeight) {
        const anyChar = chars.values().next().value;
        visibleHeight = anyChar ? anyChar.sizY * 0.4 : VSFONT_FALLBACK_VISIBLE_HEIGHT;
      }
      fonts.set(fontName, {
        fontName,
        spaceWidth,
        lineHeight,
        charSpace,
        visibleHeight,
        chars
      });
    } catch (e) {
      console.warn('Failed to parse vsfont', path, e);
    }
  }
  return fonts;
}

// Measure legacy-accurate text width using vsfont advances + charSpace + spaceWidth.
function measureVSFontLineWidth(line: string, font: VSFontMetrics): number {
  let w = 0;
  for (let i = 0; i < line.length; i++) {
    const code = line.charCodeAt(i);
    if (code === 32 || code === 9) {
      w += font.spaceWidth;
      continue;
    }
    const ch = font.chars.get(code) ?? font.chars.get(String.fromCharCode(code).toUpperCase().charCodeAt(0));
    if (!ch) continue;
    w += ch.advance || ch.sizX;
    if (i < line.length - 1) w += font.charSpace;
  }
  return w;
}

// Legacy default authoring viewport when cameraCanvasWid/Hei is missing (matches ElementPropertiesFactory).
const LEGACY_DEFAULT_CANVAS_W = 1024;
const LEGACY_DEFAULT_CANVAS_H = 640;

type CanvasSize = 'landscape' | 'square' | 'portrait';
type CameraView = { x: number; y: number; scale: number };

// Legacy `renderAspect` on each element = width/height ratio; determines project output aspect.
function resolveCanvasSize(aspect: number): CanvasSize {
  if (aspect >= 1.5) return 'landscape';
  if (aspect >= 0.85) return 'square';
  return 'portrait';
}

function referenceSize(canvasSize: CanvasSize): { w: number; h: number } {
  switch (canvasSize) {
    case 'square':
      return { w: 1080, h: 1080 };
    case 'portrait':
      return { w: 1080, h: 1920 };
    default:
      return { w: 1920, h: 1080 };
  }
}

// Invert legacy container-translate camera into new-app top-left + zoom.
function cameraFromLegacyAttrs(el: Element, refW: number, refH: number): CameraView | null {
  const camPosXAttr = el.getAttribute('cameraPositionX');
  if (camPosXAttr === null || camPosXAttr === '') return null;
  const camPosX = parseFloat(camPosXAttr);
  const camPosY = parseFloat(el.getAttribute('cameraPositionY') || '0');
  const camScale = parseFloat(el.getAttribute('cameraScale') || '0');
  if (!camScale) return null;
  let canvasW = parseFloat(el.getAttribute('cameraCanvasWid') || '0');
  let canvasH = parseFloat(el.getAttribute('cameraCanvasHei') || '0');
  if (canvasW <= 0 || canvasH <= 0) {
    canvasW = LEGACY_DEFAULT_CANVAS_W;
    canvasH = LEGACY_DEFAULT_CANVAS_H;
  }
  const scale = Math.min((refW * camScale) / canvasW, (refH * camScale) / canvasH);
  return { x: -camPosX / camScale, y: -camPosY / camScale, scale };
}

// Auto-fit camera to an element's world bounds (top-left, size), matching legacy zoomToFit.
// Legacy calls zoomToFit(5) which adds bounds.width/5 padding on all four sides.
function cameraFitToBounds(bx: number, by: number, bw: number, bh: number, refW: number, refH: number): CameraView {
  const safeW = bw > 0 ? bw : 1;
  const safeH = bh > 0 ? bh : 1;
  const pad = safeW / 5;
  const px = bx - pad;
  const py = by - pad;
  const pw = safeW + pad * 2;
  const ph = safeH + pad * 2;
  const scale = Math.min(refW / pw, refH / ph);
  return {
    x: px + pw / 2 - refW / scale / 2,
    y: py + ph / 2 - refH / scale / 2,
    scale
  };
}

// Maximum world-space dimension the new app handles comfortably.
const FIT_CANVAS_MAX = 7680;

// Translate and scale all elements so they collectively fit within FIT_CANVAS_MAX × FIT_CANVAS_MAX.
function fitProjectToCanvas(elements: VSElementModel[], refW: number, refH: number) {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const el of elements) {
    let x = el.x,
      y = el.y,
      w = 0,
      h = 0;
    if (el.type === 'Camera') {
      w = refW / el.scale;
      h = refH / el.scale;
    } else if (el.type === 'Image') {
      w = el.width * el.scaleX;
      h = el.height * el.scaleY;
    } else if (el.type === 'Text') {
      // scaleX/Y are always 1 for text; size is encoded in fontSize/width/height
      w = el.width;
      h = el.height;
    }
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  }

  if (!isFinite(minX) || !isFinite(minY)) return elements;

  const bboxW = maxX - minX;
  const bboxH = maxY - minY;
  if (bboxW <= 0 || bboxH <= 0) return elements;

  const s = Math.min(FIT_CANVAS_MAX / bboxW, FIT_CANVAS_MAX / bboxH);

  return elements.map(el => {
    const out = { ...el };
    out.x = (el.x - minX) * s;
    out.y = (el.y - minY) * s;
    if (el.type === 'Camera') {
      // Zoom out proportionally so the viewport covers the same scaled content
      (out as ScribeCameraElement).scale = el.scale / s;
    } else if (el.type === 'Image') {
      (out as ScribeImageElementModel).scaleX = el.scaleX * s;
      (out as ScribeImageElementModel).scaleY = el.scaleY * s;
    } else if (el.type === 'Text') {
      (out as ScribeTextElementModel).fontSize = el.fontSize * s;
      (out as ScribeTextElementModel).width = el.width * s;
      (out as ScribeTextElementModel).height = el.height * s;
    }
    return out;
  });
}

function buildCameraElement(view: CameraView, animationTime: number): VSElementModel {
  return {
    id: generateUUID(),
    type: 'Camera',
    locked: false,
    pauseTime: 0,
    animationTime,
    scale: view.scale,
    x: view.x,
    y: view.y,
    cameraPinned: true,
    easingType: 'power2.out'
  };
}

// Extract plain text and the first FONT SIZE from a legacy split_text drawingText HTML fragment.
function parseSplitTextHtml(html: string): {
  text: string;
  size: number | null;
} {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  const fontEl = doc.querySelector('font[size], FONT[SIZE]');
  const sizeAttr = fontEl?.getAttribute('size') ?? fontEl?.getAttribute('SIZE');
  const size = sizeAttr ? parseFloat(sizeAttr) : null;

  // Convert <P>/<BR> to line breaks so numLines / width are correct.
  doc.querySelectorAll('br, BR').forEach(n => n.replaceWith('\n'));
  const paras = doc.querySelectorAll('p, P');
  const text = paras.length
    ? Array.from(paras)
        .map(p => p.textContent || '')
        .join('\n')
    : doc.body.textContent || '';

  return { text: text.replace(/\r/g, '\n'), size };
}

// Measure rendered text width using a cached 2D canvas context.
let measureCtx: CanvasRenderingContext2D | null = null;
function measureTextWidth(lines: string[], fontSize: number, fontFamily: string): number {
  if (!measureCtx) {
    measureCtx = document.createElement('canvas').getContext('2d');
  }
  if (!measureCtx) return 0;
  measureCtx.font = `${fontSize}px "${fontFamily}", sans-serif`;
  let max = 0;
  for (const line of lines) {
    const w = measureCtx.measureText(line).width;
    if (w > max) max = w;
  }
  return max;
}

function createDefaultThumbnailImage(size = 420): string {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // 1x1 white PNG fallback for extremely constrained environments.
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO7Z4XQAAAAASUVORK5CYII=';
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  return canvas.toDataURL('image/png');
}

const DEFAULT_THUMBNAIL_IMAGE = createDefaultThumbnailImage();

async function imageBlobToSquareThumbnailDataUrl(blob: Blob, size = 420): Promise<string | null> {
  try {
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.decoding = 'async';
      const loaded = new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load thumbnail image'));
      });
      img.src = objectUrl;
      await loaded;

      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      // Use a white matte so non-square sources keep a clean thumbnail background.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const sw = img.naturalWidth || img.width || size;
      const sh = img.naturalHeight || img.height || size;
      const scale = Math.min(size / sw, size / sh);
      const dw = sw * scale;
      const dh = sh * scale;
      const dx = (size - dw) / 2;
      const dy = (size - dh) / 2;

      ctx.drawImage(img, dx, dy, dw, dh);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

async function getThumbnailFromZip(zip: JSZip): Promise<string | null> {
  const thumbPath = Object.keys(zip.files).find(path => /(^|\/)thumb\.png$/i.test(path));
  if (!thumbPath) return null;

  try {
    const thumbBlob = await zip.files[thumbPath].async('blob');
    return await imageBlobToSquareThumbnailDataUrl(thumbBlob, 420);
  } catch {
    return null;
  }
}

// Main function to process the .scribe file and return JSON payload and image/audio blobs
export async function processScribeFile(
  file: File
): Promise<{ json: ScribeModel; imageBlobs: BlobDict; audioBlobs: AudioBlobDict }> {
  const JSZip = await loadJSZip();
  const zip = await new JSZip().loadAsync(file);

  // Find XML files in the archive
  const xmlFiles: string[] = [];
  for (const [path] of Object.entries(zip.files)) {
    if (path.endsWith('.xml')) {
      xmlFiles.push(path);
    }
  }

  if (xmlFiles.length === 0) {
    throw new Error('No XML files found in archive.');
  }

  // Process each XML file to extract elements
  const elements: Array<VSElementModel> = [];
  const imageBlobs: BlobDict = {};
  const audioBlobs: AudioBlobDict = {};

  // Process the first XML file for elements (since we're dealing with one project)
  const xmlPath = xmlFiles[0];
  const xmlContent = await zip.files[xmlPath].async('string');
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

  const thumbnailImage = (await getThumbnailFromZip(zip)) || DEFAULT_THUMBNAIL_IMAGE;

  const root = xmlDoc.documentElement;
  const options = root.getAttribute('options');

  const backGroundColorRegex = /paperColour="(\d+)"/;
  const match = options?.match(backGroundColorRegex);
  const paperColourNumber = match ? match[1] : null;
  const backgroundColor = `#${parseInt(paperColourNumber || '16777215')
    .toString(16)
    .padStart(6, '0')}`;

  const requiredTypes = ['drawing', 'gif', 'split_text', 'text'];
  const elementsFromXML = xmlDoc.getElementsByTagName('element');

  const vsFonts = await loadVSFonts(zip);

  // renderAspect (width/height) is written per-element by legacy but is a project-level
  // setting; sample the first element to decide the new-app canvasSize.
  const firstAspect = parseFloat(elementsFromXML[0]?.getAttribute('renderAspect') || '1.7777777777777777');
  const canvasSize = resolveCanvasSize(firstAspect);
  const { w: refW, h: refH } = referenceSize(canvasSize);

  // Legacy transitionTime is the duration to move FROM this element TO the next,
  // so it becomes the NEXT camera's animationTime. Camera 0 has no incoming move.
  let prevTransitionMs = 0;

  for (let i = 0; i < elementsFromXML.length; i++) {
    const el = elementsFromXML[i];
    const elementType = el.getAttribute('elementType');

    if (!elementType || !requiredTypes.includes(elementType)) {
      console.warn(`Element at index ${i} is missing 'elementType' attribute. Skipping.`);
      continue;
    }

    const additionScale = parseFloat(el.getAttribute('theScale') || '1');

    // Create a common structure for all elements
    const isImageElement = elementType === 'drawing' || elementType === 'gif';

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementData: any = {
      id: el.getAttribute('elementID') || `element_${i}`,
      type: isImageElement ? 'Image' : 'Text',
      width: 0, // Will be updated based on SVG dimensions when available
      height: 0,
      x: parseFloat(el.getAttribute('currentPosX') || '0'),
      y: parseFloat(el.getAttribute('currentPosY') || '0'),
      scaleX: parseFloat(el.getAttribute('scalesX') || '1') * additionScale,
      scaleY: parseFloat(el.getAttribute('scalesY') || '1') * additionScale,
      angle: parseFloat(el.getAttribute('rotation') || '0'),
      // Legacy stores element center in currentPosX/Y; we convert to top-left below.
      originX: 'left',
      originY: 'top',
      flipX: el.getAttribute('flipHoriz') === 'yes',
      flipY: el.getAttribute('flipVert') === 'yes',
      locked: el.getAttribute('locked') === 'yes',
      unlockedRatio: true,
      hidden: el.getAttribute('visible') === 'false',
      outOfCamera: false,
      pauseTime: msToSec(parseFloat(el.getAttribute('pauseTime') || '0')),
      animationTime: msToSec(parseFloat(el.getAttribute('targetTime') || '0')),
      emphasisAnimationTime: 0,
      exitAnimationTime: 0,
      emphasisAnimationLoops: 0,
      cursorId: 'default',
      opacity: parseFloat(el.getAttribute('opacity') || '1'),
      entranceAnimation: {
        id: 'pen-draw',
        config: {}
      },
      isPremium: false,
      useContentSvgViewbox: false,
      viewboxAttributes: {
        contents: {
          viewBox: '0 0 1000 1000',
          width: 1000,
          height: 1000
        },
        original: {
          viewBox: '0 0 1000 1000',
          width: 1000,
          height: 1000
        }
      },
      // For images (drawing type), we'll add image data (but not actual content yet)
      image: isImageElement
        ? {
            contentType: elementType === 'gif' ? 'image/gif' : 'image/svg+xml',
            provider: 'user',
            filename: elementType === 'gif' ? `element_${i}.gif` : `element_${i}.svg`,
            assetId:
              elementType === 'gif'
                ? `file::${el.getAttribute('elementID')}.gif`
                : `file::${el.getAttribute('elementID')}.svg`
          }
        : null
    };

    const drawStyle = el.getAttribute('drawStyle');
    if (drawStyle === 'draw_style_movein') {
      const compassPointMap: Record<string, string> = {
        '0': '@tl',
        '1': '@t',
        '2': '@tr',
        '3': '@r',
        '4': '@br',
        '5': '@b',
        '6': '@bl',
        '7': '@l'
      };
      // handle move in animation
      elementData.entranceAnimation = undefined;
      elementData.entranceTween = {
        id: 'move-in',
        tweens: [
          {
            position: compassPointMap[el.getAttribute('movinCompass') || '0'],
            ease: 'power1'
          }
        ]
      };
    } else if (drawStyle === 'draw_style_fadein') {
      // handle fade in animation
      elementData.entranceAnimation = undefined;
      elementData.entranceTween = {
        id: 'fade-in',
        tweens: [
          {
            alpha: 0
          }
        ]
      };
    }

    // Special handling for text elements
    if (elementType === 'split_text' || elementType === 'text') {
      elementData.type = 'Text';
      const drawingText = el.getAttribute('drawingText') || '';
      const originalFontname = el.getAttribute('fontName');
      const fontName =
        fontFilesManifest.fonts.find(f => f.displayName === originalFontname)?.displayName || 'Noto Sans';
      const scalesYRaw = parseFloat(el.getAttribute('scalesY') || '1');
      const scalesXRaw = parseFloat(el.getAttribute('scalesX') || '1');
      const theScale = parseFloat(el.getAttribute('theScale') || '1');

      let plainText: string;
      let fontSize: number;
      let lineHeightPx: number;
      let widthWorld: number | null = null;

      if (elementType === 'split_text') {
        // TLF path: SIZE lives inside the HTML; whole sprite is scaled by scalesY × theScale.
        const parsed = parseSplitTextHtml(drawingText);
        plainText = parsed.text;
        const htmlSize = parsed.size ?? 18;
        fontSize = Math.round(htmlSize * scalesYRaw * theScale);
        lineHeightPx = fontSize * TLF_LINE_HEIGHT_RATIO;

        const regex = /#.{6}/gim;
        const htmlColors = (drawingText.match(regex) || [])
          .map(c => c.toUpperCase())
          .filter(color => color !== '#000000');

        elementData.fill = htmlColors[0] || '#000000';
      } else {
        // vsfont path: each glyph is drawn in a padded SVG (sizY, typically 175) but the
        // visible glyph height is ~lineHeight from the font XML. Prefer per-font metrics
        // when the vsfont is bundled; otherwise fall back to a tuned constant.
        plainText = drawingText.replace(/&#xD;/g, '\n').replace(/\r/g, '\n');
        const font = vsFonts.get(fontName) ?? (vsFonts.size === 1 ? vsFonts.values().next().value : undefined);
        const visibleH = font?.visibleHeight ?? VSFONT_FALLBACK_VISIBLE_HEIGHT;
        fontSize = Math.round(visibleH * theScale * scalesYRaw);
        lineHeightPx = fontSize;
        if (font) {
          const lines = plainText.split('\n');
          let maxLineAdvance = 0;
          for (const line of lines) {
            const w = measureVSFontLineWidth(line, font);
            if (w > maxLineAdvance) maxLineAdvance = w;
          }
          widthWorld = maxLineAdvance * theScale * scalesXRaw;
        }
        elementData.fill = intToHexColor(parseInt(el.getAttribute('textColour') || '0', 10));
      }

      elementData.text = plainText;
      elementData.font = { label: fontName, value: fontName };
      elementData.textAlign = el.getAttribute('textAlign') || 'left';
      elementData.fontSize = fontSize;

      const lines = plainText.split('\n').filter((l: string) => l.length > 0);
      const numLines = lines.length || 1;
      elementData.height = lineHeightPx * numLines;
      elementData.width = widthWorld ?? measureTextWidth(lines, fontSize, fontName);

      // Text sizing is carried by fontSize in the new app, not scaleX/Y.
      elementData.scaleX = 1;
      elementData.scaleY = 1;
    }

    // For images, extract dimensions and blob content from legacy drawingXML.
    if (isImageElement) {
      const availableRecolours = el.getAttribute('availableRecolours') || ''; // recolor defaults
      const recolouringSchemes = el.getAttribute('recolouringSchemes') || ''; // recolor overrides

      if (availableRecolours) {
        elementData.recolorAvailable = true;
        elementData._recolorDefaults = {};
        elementData.image.recolor = {};

        const hairRegex = /vs-hair\s+(\d+)/gm;
        const hairOverrideRegex = /vs-hair\s+(\d+)/gm;
        const hairDefaultMatch = hairRegex.exec(availableRecolours);
        const hairOverrideMatch = hairOverrideRegex.exec(recolouringSchemes);
        if (hairDefaultMatch) {
          const hairDefault = hairDefaultMatch[1];
          const hairColorHex = intToHexColor(parseInt(hairDefault));

          elementData._recolorDefaults.hairColor = hairColorHex;
        }
        if (hairOverrideMatch) {
          const hairOverride = hairOverrideMatch[1];
          const hairColorHex = intToHexColor(parseInt(hairOverride));

          elementData.image.recolor.hairColor = hairColorHex;
        }

        const skinRegex = /vs-skin\s+(\d+)/gm;
        const skinOverrideRegex = /vs-skin\s+(\d+)/gm;
        const skinDefaultMatch = skinRegex.exec(availableRecolours);
        const skinOverrideMatch = skinOverrideRegex.exec(recolouringSchemes);
        if (skinDefaultMatch) {
          const skinDefault = skinDefaultMatch[1];
          const skinColorHex = intToHexColor(parseInt(skinDefault));

          elementData._recolorDefaults.skinColor = skinColorHex;
        }
        if (skinOverrideMatch) {
          const skinOverride = skinOverrideMatch[1];
          const skinColorHex = intToHexColor(parseInt(skinOverride));

          elementData.image.recolor.skinColor = skinColorHex;
        }

        const primaryRegex = /vs-primary\s+(\d+)/gm;
        const primaryOverrideRegex = /vs-primary\s+(\d+)/gm;
        const primaryDefaultMatch = primaryRegex.exec(availableRecolours);
        const primaryOverrideMatch = primaryOverrideRegex.exec(recolouringSchemes);
        if (primaryDefaultMatch) {
          const primaryDefault = primaryDefaultMatch[1];
          const primaryColorHex = intToHexColor(parseInt(primaryDefault));

          elementData._recolorDefaults.primaryColor = primaryColorHex;
        }
        if (primaryOverrideMatch) {
          const primaryOverride = primaryOverrideMatch[1];
          const primaryColorHex = intToHexColor(parseInt(primaryOverride));

          elementData.image.recolor.primaryColor = primaryColorHex;
        }

        const secondaryRegex = /vs-secondary\s+(\d+)/gm;
        const secondaryOverrideRegex = /vs-secondary\s+(\d+)/gm;
        const secondaryDefaultMatch = secondaryRegex.exec(availableRecolours);
        const secondaryOverrideMatch = secondaryOverrideRegex.exec(recolouringSchemes);
        if (secondaryDefaultMatch) {
          const secondaryDefault = secondaryDefaultMatch[1];
          const secondaryColorHex = intToHexColor(parseInt(secondaryDefault));

          elementData._recolorDefaults.secondaryColor = secondaryColorHex;
        }
        if (secondaryOverrideMatch) {
          const secondaryOverride = secondaryOverrideMatch[1];
          const secondaryColorHex = intToHexColor(parseInt(secondaryOverride));

          elementData.image.recolor.secondaryColor = secondaryColorHex;
        }

        const highlightRegex = /vs-highlight\s+(\d+)/gm;
        const highlightOverrideRegex = /vs-highlight\s+(\d+)/gm;
        const highlightDefaultMatch = highlightRegex.exec(availableRecolours);
        const highlightOverrideMatch = highlightOverrideRegex.exec(recolouringSchemes);
        if (highlightDefaultMatch) {
          const highlightDefault = highlightDefaultMatch[1];
          const highlightColorHex = intToHexColor(parseInt(highlightDefault));

          elementData._recolorDefaults.highlightColor = highlightColorHex;
        }
        if (highlightOverrideMatch) {
          const highlightOverride = highlightOverrideMatch[1];
          const highlightColorHex = intToHexColor(parseInt(highlightOverride));

          elementData.image.recolor.highlightColor = highlightColorHex;
        }
      }

      const colourEffect = parseInt(el.getAttribute('colourEffect') || '0', 10);
      if (colourEffect) {
        if (colourEffect === 1) {
          // outline mode
          elementData.recolorAvailable = true;
          elementData.image.recolor = elementData.image.recolor || {};

          elementData.image.recolor.skinColor = '#FFFFFF'; // default to white for outline mode
          elementData.image.recolor.hairColor = '#FFFFFF'; // default to white for outline mode
          elementData.image.recolor.primaryColor = '#FFFFFF'; // default to white for outline mode
          elementData.image.recolor.secondaryColor = '#FFFFFF'; // default to white for outline mode
          elementData.image.recolor.highlightColor = '#FFFFFF'; // default to white for outline mode
        }
      }

      if (elementType === 'drawing') {
        // Try to parse the SVG to extract dimensions
        try {
          let rawSvg = el.getAttribute('drawingXML') || '';
          let cleanSvg = rawSvg.replaceAll(/\baaa\b/g, 'xml');

          // Strip namespace prefixes from SVG elements to make them compatible
          // Replace svg: prefix with just svg, etc.
          cleanSvg = cleanSvg.replace(/svg:/g, '');

          const isAdobeIllustratorFile =
            cleanSvg.includes('<foreignObject') && cleanSvg.includes('i:extraneous="self"');

          const doc = new DOMParser().parseFromString(cleanSvg, 'text/html');

          const svgEl = doc.querySelector('svg');
          if (svgEl) {
            if (isAdobeIllustratorFile) {
              const svgContents = svgEl.querySelector('g');
              if (svgContents) {
                svgEl.innerHTML = svgContents.innerHTML;
              }
            }

            const origWidth = parseFloat(svgEl.getAttribute('width') || '1000');
            const origHeight = parseFloat(svgEl.getAttribute('height') || '1000');

            // Adopt into the main document so getBBox() can measure rendered content
            const svg = document.adoptNode(svgEl) as SVGGraphicsElement & Element;
            svg.removeAttribute('viewBox');
            svg.setAttribute('width', '1');
            svg.setAttribute('height', '1');
            svg.setAttribute('style', 'position:absolute;visibility:hidden');
            document.body.appendChild(svg);

            let bx = 0,
              by = 0,
              bw = origWidth,
              bh = origHeight;
            try {
              // Wrap all children in a <g> so getBBox() returns the unified content bounds
              const g = document.createElementNS('http://www.w3.org/2000/svg', 'g') as SVGGraphicsElement;
              Array.from(svg.childNodes).forEach(c => g.appendChild(c));
              svg.appendChild(g);
              const bbox = g.getBBox();
              if (bbox.width > 0 && bbox.height > 0) {
                bx = bbox.x;
                by = bbox.y;
                bw = bbox.width;
                bh = bbox.height;
              }
              Array.from(g.childNodes).forEach(c => svg.appendChild(c));
              svg.removeChild(g);
            } catch {}

            document.body.removeChild(svg);
            svg.removeAttribute('style');

            // Pad the viewBox slightly so strokes / curves that legacy Flash
            // rendered outside the reported bbox (and filter margins we don't
            // reproduce) don't get clipped. 2% of the largest side, min 4 units.
            const pad = Math.max(4, Math.max(bw, bh) * 0.02);
            const pbx = bx - pad;
            const pby = by - pad;
            const pbw = bw + pad * 2;
            const pbh = bh + pad * 2;

            const viewBoxStr = `${pbx} ${pby} ${pbw} ${pbh}`;
            svg.setAttribute('viewBox', viewBoxStr);
            svg.setAttribute('width', String(pbw));
            svg.setAttribute('height', String(pbh));

            elementData.width = pbw;
            elementData.height = pbh;
            elementData.viewboxAttributes = {
              contents: { viewBox: viewBoxStr, width: pbw, height: pbh },
              original: { viewBox: viewBoxStr, width: pbw, height: pbh }
            };

            const blob = new Blob([svg.outerHTML], { type: 'image/svg+xml' });
            imageBlobs[elementData.image.assetId] = blob;
          }
        } catch (e) {
          // If parsing SVG fails, keep defaults
          console.error('Failed to parse SVG:', e);
        }
      }

      if (elementType === 'gif') {
        try {
          const drawingXML = el.getAttribute('drawingXML') || '';
          const dataDoc = new DOMParser().parseFromString(drawingXML, 'text/xml');
          const gifAttr =
            dataDoc.documentElement.getAttribute('gif') || dataDoc.querySelector('data')?.getAttribute('gif') || '';

          if (!gifAttr) {
            console.warn('Missing gif payload in drawingXML', elementData.id);
          } else {
            const m = gifAttr.match(/^data:([^;]+);base64,(.+)$/i);
            const contentType = m?.[1] || 'image/gif';
            const base64Payload = m?.[2] || gifAttr;
            const bytes = base64ToBytes(base64Payload);

            if (!bytes) {
              console.warn('Invalid gif base64 payload', elementData.id);
            } else {
              const dims = readGifDimensions(bytes);
              if (dims) {
                elementData.width = dims.width;
                elementData.height = dims.height;
                const viewBoxStr = `0 0 ${dims.width} ${dims.height}`;
                elementData.viewboxAttributes = {
                  contents: {
                    viewBox: viewBoxStr,
                    width: dims.width,
                    height: dims.height
                  },
                  original: {
                    viewBox: viewBoxStr,
                    width: dims.width,
                    height: dims.height
                  }
                };
              }

              if (elementData.image) {
                elementData.image.contentType = contentType;
              }
              const gifBuffer = new ArrayBuffer(bytes.byteLength);
              new Uint8Array(gifBuffer).set(bytes);
              const blob = new Blob([gifBuffer], { type: contentType });
              imageBlobs[elementData.image.assetId] = blob;
            }
          }
        } catch (e) {
          console.error('Failed to parse GIF:', e);
        }
      }
    }

    // Adjust position from center-based to top-left based on width, height and scale
    // When the width and height are known, we adjust x and y by subtracting half of each value
    // If rotation is applied, we need to rotate the offset vector from center to top-left
    const adjustedElementData = { ...elementData };

    // Apply scaling factors to dimensions for proper position calculation
    const scaledWidth = elementData.width * elementData.scaleX;
    const scaledHeight = elementData.height * elementData.scaleY;

    // Adjust position if we have dimensions
    if (elementData.width && elementData.height) {
      // Offset from center to top-left in unrotated space
      const offsetX = -scaledWidth / 2;
      const offsetY = -scaledHeight / 2;

      // If there's rotation, rotate the offset vector
      if (elementData.angle !== 0) {
        const angleRad = (elementData.angle * Math.PI) / 180;
        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);

        const rotatedOffsetX = offsetX * cos - offsetY * sin;
        const rotatedOffsetY = offsetX * sin + offsetY * cos;

        adjustedElementData.x = elementData.x + rotatedOffsetX;
        adjustedElementData.y = elementData.y + rotatedOffsetY;
      } else {
        adjustedElementData.x = elementData.x + offsetX;
        adjustedElementData.y = elementData.y + offsetY;
      }
    }

    // Calculate camera bounds based on element center (stable reference point)
    // For rotated elements, the bounds expand but are calculated from the same center
    let camBx = elementData.x - scaledWidth / 2;
    let camBy = elementData.y - scaledHeight / 2;
    let camBw = scaledWidth || elementData.width || 1;
    let camBh = scaledHeight || elementData.height || 1;

    if (elementData.angle !== 0 && elementData.width && elementData.height) {
      // Calculate rotated bounding box by rotating the four corners around the element center
      const angleRad = (elementData.angle * Math.PI) / 180;
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);

      // Element center
      const centerX = elementData.x;
      const centerY = elementData.y;

      // Corners in center-relative space
      const corners = [
        { x: -scaledWidth / 2, y: -scaledHeight / 2 },
        { x: scaledWidth / 2, y: -scaledHeight / 2 },
        { x: -scaledWidth / 2, y: scaledHeight / 2 },
        { x: scaledWidth / 2, y: scaledHeight / 2 }
      ];

      // Rotate corners and find bounding box
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      for (const corner of corners) {
        const rotX = corner.x * cos - corner.y * sin;
        const rotY = corner.x * sin + corner.y * cos;
        const worldX = centerX + rotX;
        const worldY = centerY + rotY;

        minX = Math.min(minX, worldX);
        minY = Math.min(minY, worldY);
        maxX = Math.max(maxX, worldX);
        maxY = Math.max(maxY, worldY);
      }

      camBx = minX;
      camBy = minY;
      camBw = maxX - minX;
      camBh = maxY - minY;
    }

    const view = cameraFromLegacyAttrs(el, refW, refH) ?? cameraFitToBounds(camBx, camBy, camBw, camBh, refW, refH);
    elements.push(buildCameraElement(view, msToSec(prevTransitionMs)));
    elements.push(adjustedElementData);

    prevTransitionMs = parseFloat(el.getAttribute('transitionTime') || '0');
  }

  const fittedElements = fitProjectToCanvas(elements, refW, refH);

  // Build scene structure with all elements
  const scene = {
    id: generateUUID(),
    elementIds: fittedElements.map(el => el.id),
    settings: {
      pauseTime: 0.5,
      animationTime: 2.5,
      emphasisAnimationTime: 3.5,
      exitAnimationTime: 2.5,
      backgroundColor: backgroundColor,
      emphasisAnimationLoops: 1,
      sceneTransitionTime: 0,
      textStylingConfig: {
        font: {
          label: 'Open Sans',
          value: 'Open Sans'
        },
        align: 'left',
        fill: '#131515',
        fontSize: 120,
        fontWeight: 'normal',
        fontStyle: 'normal',
        opacity: 1,
        lineHeight: 1.2
      }
    },
    active: true,
    audioLayerIds: [] as string[],
    thumbnailImage
  };

  // Create the complete project structure
  const projectJSON = {
    id: 439220,
    title: file.name ? file.name.replace('.scribe', '') + ' (Converted Legacy Project)' : 'Converted Project',
    elements: fittedElements,
    canvasSize: canvasSize,
    thumbnailImage,
    createdOn: new Date(),
    settings: {
      pauseTime: 0.5,
      animationTime: 2.5,
      emphasisAnimationTime: 3.5,
      exitAnimationTime: 2.5,
      backgroundColor: backgroundColor,
      emphasisAnimationLoops: 1,
      sceneTransitionTime: 0,
      textStylingConfig: {
        font: {
          label: 'Open Sans',
          value: 'Open Sans'
        },
        align: 'left',
        fill: '#131515',
        fontSize: 120,
        fontWeight: 'normal',
        fontStyle: 'normal',
        opacity: 1,
        lineHeight: 1.2
      }
    },
    source: {
      type: 'blank'
    },
    scenes: [scene],
    audioClips: [],
    audioLayers: [],
    pinAllCameras: true,
    projectAudioLayerIds: [],
    nextSelectedElementIds: fittedElements.map(el => el.id)
  } as ScribeModel;

  const backingTrackPath = root.getAttribute('backingTrack');
  const voiceOverPath = root.getAttribute('voiceOver');
  // Process audio files
  if (backingTrackPath) {
    const content = await zip.files[backingTrackPath].async('blob');
    const assetName = root.getAttribute('backingTrackTitle') || 'imported audio';
    audioBlobs[backingTrackPath] = { file: content, role: 'music', assetName };

    const clipId = generateUUID(); // Generate a unique ID for the audio clip
    const clip = {
      assetId: backingTrackPath,
      type: 'project',
      source: 'uploaded',
      volume: parseFloat(root.getAttribute('backingTrackVolume') || '1'),
      filename: assetName,
      startTime: 0,
      startOffset: 0,
      id: clipId,
      fadeOutDurationSeconds: 0
    } as unknown as AudioClip;

    projectJSON.audioClips?.push(clip);

    const layerId = generateUUID(); // Generate a unique ID for the audio layer
    projectJSON.audioLayers.push({
      id: layerId,
      audioClipIds: [clipId],
      getAudioClips: () => [clip]
    });

    projectJSON.scenes[0].audioLayerIds?.push(layerId);
  }

  if (voiceOverPath) {
    const content = await zip.files[voiceOverPath].async('blob');
    const assetName = root.getAttribute('name') ? root.getAttribute('name') + ' voiceover' : 'imported audio';
    audioBlobs[voiceOverPath] = { file: content, role: 'voiceover', assetName };

    const clipId = generateUUID(); // Generate a unique ID for the audio clip
    const clip = {
      assetId: voiceOverPath,
      type: 'project',
      source: 'uploaded',
      volume: parseFloat(root.getAttribute('voiceOverVolume') || '1'),
      filename: assetName,
      startTime: 0,
      startOffset: 0,
      id: clipId,
      fadeOutDurationSeconds: 0
    } as unknown as AudioClip;

    projectJSON.audioClips?.push(clip);

    const layerId = generateUUID(); // Generate a unique ID for the audio layer
    projectJSON.audioLayers.push({
      id: layerId,
      audioClipIds: [clipId],
      getAudioClips: () => [clip]
    });

    projectJSON.scenes[0].audioLayerIds?.push(layerId);
  }

  return {
    json: projectJSON,
    imageBlobs,
    audioBlobs
  };
}
